/**
 * Centralized portal resolution.
 *
 * SINGLE SOURCE OF TRUTH for deciding which portal a user belongs to.
 * Every routing decision in the app (App.tsx ProtectedRoute, Auth.tsx
 * post-login redirect, individual portal guards) MUST delegate here so
 * users with overlapping roles never flip between portals between renders
 * or between entry points.
 *
 * Priority ladder (highest → lowest):
 *   1. No user                       → 'auth'
 *   2. isAttorney                    → 'attorney'   (attorneys win over admin auto-redirect)
 *   3. isInHouseCounsel              → 'in-house'   (wins over auto-provisioned client record)
 *   4. isAdmin (pure admin)          → 'admin'
 *   5. isDelegate || isClient        → 'client'
 *   6. fallback                      → 'client'
 *
 * NOTE: The historical convention (see App.tsx ProtectedRoute and
 * mem://auth/dual-role-portal-redirection) is that an attorney who is
 * also admin lands in the attorney portal. The admin dashboard remains
 * reachable via direct navigation but is never the auto-resolved target
 * for an attorney+admin user.
 */

export type Portal = 'auth' | 'attorney' | 'in-house' | 'admin' | 'client';

export interface PortalInputs {
  hasUser: boolean;
  isAttorney: boolean;
  isInHouseCounsel: boolean;
  isAdmin: boolean;
  isDelegate: boolean;
  isClient: boolean;
}

/** Pure function — no hooks, no side effects. Safe to use anywhere. */
export const resolvePortal = (inputs: PortalInputs): Portal => {
  if (!inputs.hasUser) return 'auth';
  if (inputs.isAttorney) return 'attorney';
  if (inputs.isInHouseCounsel) return 'in-house';
  if (inputs.isAdmin) return 'admin';
  if (inputs.isDelegate || inputs.isClient) return 'client';
  return 'client';
};

/** Default landing path for a resolved portal. */
export const portalPath = (portal: Portal): string => {
  switch (portal) {
    case 'auth':     return '/auth';
    case 'attorney': return '/attorney-portal';
    case 'in-house': return '/in-house-portal';
    case 'admin':    return '/admin-dashboard';
    case 'client':   return '/app';
  }
};

import { useAuth } from '@/context/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

export interface ResolvedPortal {
  portal: Portal;
  path: string;
  loading: boolean;
}

/**
 * React hook wrapper. Returns the resolved portal plus a `loading` flag
 * that callers MUST respect before redirecting — resolving while role
 * data is still loading is the original cause of the in-house/client flip.
 */
export const useResolvedPortal = (): ResolvedPortal => {
  const { user, loading: authLoading } = useAuth();
  const {
    isAttorney,
    isInHouseCounsel,
    isAdmin,
    isDelegate,
    isClient,
    loading: roleLoading,
  } = useUserRole();

  const loading = authLoading || (!!user && roleLoading);

  const portal = resolvePortal({
    hasUser: !!user,
    isAttorney,
    isInHouseCounsel,
    isAdmin,
    isDelegate,
    isClient,
  });

  return { portal, path: portalPath(portal), loading };
};